"use client";

import Image from "next/image";
import {
  ArrowUpRight,
  ArrowUp,
  Check,
  CheckCircle2,
  ChevronDown,
  FileText,
  Languages,
  Mic,
  MoreHorizontal,
  Plus,
  Sparkles,
  SquarePen,
} from "lucide-react";
import { useTranslation } from "react-i18next";
import HomeSection from "@/components/marketing/HomeSection";
import { ChatGPTIcon } from "./HeroAvailability";
import linkedinLogo from "@/public/assets/Linkedin-logo.png";
import indeedLogo from "@/public/assets/Indeed-Logo.png";
import upworkLogo from "@/public/assets/Upwork-logo.png";
import glassdoorLogo from "@/public/assets/Glassdoor-Logo.png";
import fiverrLogo from "@/public/assets/fiverr-logo.png";
import styles from "./CapabilitiesSection.module.css";

const panel =
  "rounded-xl border border-slate-200/80 bg-white/95 text-slate-700 shadow-[0_16px_45px_-20px_rgba(15,23,42,0.3)] backdrop-blur-xl dark:border-white/15 dark:bg-slate-900/95 dark:text-slate-200";
const panelHeader =
  "flex items-center justify-between gap-2 border-b border-slate-100 px-4 py-3 text-[11px] font-medium dark:border-white/10";

const platforms = [
  { name: "LinkedIn", logo: linkedinLogo },
  { name: "Indeed", logo: indeedLogo },
  { name: "Upwork", logo: upworkLogo },
  { name: "Glassdoor", logo: glassdoorLogo },
  { name: "Fiverr", logo: fiverrLogo },
] as const;

const capabilities = [
  { key: "LANGUAGES", Preview: LanguagesPreview, color: styles.cyan },
  { key: "PLATFORMS", Preview: PlatformsPreview, color: styles.blue },
  { key: "FEEDBACK", Preview: FeedbackPreview, color: styles.rose },
  { key: "LETTER", Preview: LetterPreview, color: styles.mint },
  { key: "AI_CHAT", Preview: AIChatPreview, color: styles.amber },
  { key: "INTERVIEWS", Preview: InterviewsPreview, color: styles.cyan },
] as const;

function LanguagesPreview() {
  const { t } = useTranslation();

  return (
    <div className="relative h-full px-5 pt-14">
      <div className={`${panel} w-[82%]`}>
        <div className={panelHeader}><span className="flex items-center gap-2"><FileText className="h-3.5 w-3.5" />{t("HOME_CAPABILITIES_PREVIEW_RESUME")}</span></div>
        <div className="space-y-4 p-4">
          <p className="text-[11px] font-semibold">{t("HOME_CAPABILITIES_PREVIEW_PROFILE")}</p>
          <p className="text-[11px] leading-relaxed">{t("HOME_CAPABILITIES_PREVIEW_SUMMARY")}</p>
          <div className="space-y-2">
            <div className="h-1.5 w-full rounded bg-slate-200 dark:bg-slate-700" />
            <div className="h-1.5 w-4/5 rounded bg-slate-200 dark:bg-slate-700" />
          </div>
          <span className="inline-flex items-center gap-1.5 rounded-lg bg-emerald-50 px-2 py-1.5 text-[10px] text-emerald-700 dark:bg-emerald-400/10 dark:text-emerald-300"><Check className="h-3 w-3" />{t("HOME_CAPABILITIES_PREVIEW_FORMAT")}</span>
        </div>
      </div>
      <div className={`${panel} absolute right-5 top-7 w-[48%] max-w-48 p-3`}>
        <p className="mb-3 flex items-center gap-1.5 text-[10px]"><Languages className="h-3 w-3" />{t("HOME_CAPABILITIES_PREVIEW_LANGUAGE")}</p>
        {(["EN", "ES"] as const).map((language) => (
          <div key={language} className={`flex items-center justify-between gap-2 rounded-md px-2 py-2 text-[11px] ${language === "ES" ? "bg-slate-100 dark:bg-white/10" : ""}`}>
            <span className="flex items-center gap-2"><span>{language === "EN" ? "🇺🇸" : "🇪🇸"}</span>{t(`HOME_CAPABILITIES_PREVIEW_${language}`)}</span>
            {language === "ES" && <Check className="h-3 w-3 shrink-0 text-indigo-500 dark:text-indigo-300" />}
          </div>
        ))}
      </div>
    </div>
  );
}

function PlatformsPreview() {
  return (
    <div className="relative flex h-full items-center gap-0 px-6 pb-2">
      <div className={`${panel} flex h-12 w-12 shrink-0 items-center justify-center rounded-xl`}>
        <Image src="/landfast-logo.svg" alt="" width={25} height={25} className="h-6 w-6" />
      </div>
      <div className="h-px min-w-4 flex-1 bg-white/90 dark:bg-white/40" />
      <div className={`${panel} w-[74%] max-w-64 overflow-hidden py-1`}>
        {platforms.map(({ name, logo }) => (
          <div key={name} className="flex items-center gap-2.5 border-b border-slate-100 px-3 py-3 last:border-0 dark:border-white/10">
            <span className="flex h-5 w-6 shrink-0 items-center justify-center rounded bg-white">
              <Image src={logo} alt="" sizes="24px" className="h-auto max-h-4 w-5 object-contain" />
            </span>
            <span className="min-w-0 flex-1 text-[11px] font-medium">{name}</span>
            <span className="flex h-4 w-6 shrink-0 items-center justify-center rounded-full bg-sky-500 text-white"><ArrowUpRight className="h-2.5 w-2.5" /></span>
          </div>
        ))}
      </div>
    </div>
  );
}

function FeedbackPreview() {
  const { t } = useTranslation();

  return (
    <div className="h-full px-7 pt-7">
      <div className={panel}>
        <div className={panelHeader}><span className="flex items-center gap-2"><Sparkles className="h-3.5 w-3.5" />{t("HOME_CAPABILITIES_PREVIEW_FEEDBACK")}</span><MoreHorizontal className="h-3.5 w-3.5" /></div>
        <div className="px-4 py-3">
          <p className="mb-4 text-[11px] text-slate-500 dark:text-slate-400">{t("HOME_CAPABILITIES_PREVIEW_REVIEW")}</p>
          <div className="mb-2 flex items-center justify-between text-[11px]"><span>{t("HOME_CAPABILITIES_PREVIEW_IMPROVEMENTS")}</span><ChevronDown className="h-3 w-3" /></div>
          <div className="ml-1 space-y-4 border-l border-slate-200 py-2 pl-3 dark:border-white/15">
            {(["KEYWORDS", "IMPACT", "STRUCTURE"] as const).map((item) => (
              <div key={item} className="flex items-start gap-2 text-[11px] leading-relaxed"><CheckCircle2 className="mt-0.5 h-3 w-3 shrink-0 text-emerald-500" /><span>{t(`HOME_CAPABILITIES_PREVIEW_${item}`)}</span></div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

function LetterPreview() {
  const { t } = useTranslation();

  return (
    <div className="h-full px-6 pt-6">
      <div className={`${panel} mx-auto max-w-[310px] overflow-hidden`}>
        <div className={panelHeader}>
          <span className="flex items-center gap-2"><FileText className="h-3.5 w-3.5" />{t("HOME_CAPABILITIES_PREVIEW_LETTER")}</span>
          <Sparkles className="h-3.5 w-3.5 text-indigo-500 dark:text-indigo-300" />
        </div>
        <div className="space-y-3 px-4 py-4 text-[11px] leading-relaxed">
          <p className="inline-flex rounded-full bg-indigo-50 px-2.5 py-1 text-[10px] text-indigo-700 dark:bg-indigo-400/15 dark:text-indigo-200">{t("HOME_CAPABILITIES_PREVIEW_LETTER_ROLE")}</p>
          <p className="font-semibold text-slate-800 dark:text-slate-100">{t("HOME_CAPABILITIES_PREVIEW_LETTER_GREETING")}</p>
          <p>{t("HOME_CAPABILITIES_PREVIEW_LETTER_BODY")}</p>
          <p className="border-t border-slate-100 pt-3 font-medium dark:border-white/10">{t("HOME_CAPABILITIES_PREVIEW_LETTER_SIGNOFF")}</p>
        </div>
      </div>
    </div>
  );
}

function AIChatPreview() {
  const { t } = useTranslation();

  return (
    <div data-capability-preview="ai-chat" className="h-full px-5 pt-5">
      <div className={`${panel} overflow-hidden`}>
        <div className="flex h-10 items-center gap-1.5 border-b border-slate-100 px-3 dark:border-white/10">
          <span className="text-[11px] font-semibold text-slate-900 dark:text-slate-100">ChatGPT</span>
          <ChevronDown className="h-2.5 w-2.5 text-slate-400" />
          <SquarePen className="ml-auto h-3.5 w-3.5 text-slate-500 dark:text-slate-400" />
        </div>
        <div className="p-2.5">
          <div className="ml-auto w-fit max-w-[94%] rounded-2xl bg-slate-100 px-2.5 py-1.5 text-[10px] leading-relaxed text-slate-800 dark:bg-slate-800 dark:text-slate-100">
            <span className="rounded bg-indigo-100/80 px-1 font-semibold text-indigo-700 dark:bg-indigo-400/15 dark:text-indigo-200">@landfast</span>{" "}{t("HOME_CAPABILITIES_PREVIEW_CHAT_REQUEST")}
          </div>
          <div className="mt-2 flex items-start gap-2">
            <ChatGPTIcon className="mt-0.5 h-4 w-4 shrink-0 text-slate-800 dark:text-slate-200" />
            <div className="min-w-0 flex-1">
              <span className="inline-flex items-center gap-1 rounded-md border border-slate-200 bg-slate-50 px-1.5 py-0.5 text-[8px] font-medium dark:border-white/10 dark:bg-white/5"><Sparkles className="h-2.5 w-2.5 text-indigo-500 dark:text-indigo-300" />LandFast<Check className="h-2.5 w-2.5 text-emerald-500" /></span>
              <p className="mt-1.5 text-[10px] leading-relaxed">{t("HOME_CAPABILITIES_PREVIEW_CHAT_RESPONSE")}</p>
              <div className="mt-1.5 rounded-lg border border-slate-200 bg-white p-2 dark:border-white/10 dark:bg-white/[0.03]">
                <div className="flex items-center justify-between gap-2"><p className="truncate text-[10px] font-semibold text-slate-900 dark:text-white">{t("HOME_CAPABILITIES_PREVIEW_CHAT_ROLE")}</p><ArrowUpRight className="h-3 w-3 shrink-0 text-slate-400" /></div>
                <div className="mt-1 flex flex-wrap items-center justify-between gap-1 text-[8px]"><span className="text-slate-500 dark:text-slate-400">Northstar · {t("HOME_CAPABILITIES_PREVIEW_CHAT_REMOTE")}</span><span className="font-medium text-emerald-700 dark:text-emerald-300">94% {t("HOME_CAPABILITIES_PREVIEW_CHAT_MATCH")}</span></div>
              </div>
            </div>
          </div>
          <div className="mt-2 flex items-center gap-2 rounded-2xl border border-slate-200/70 bg-slate-50 px-2 py-1.5 dark:border-white/10 dark:bg-slate-800">
            <Plus className="h-3 w-3 shrink-0 text-slate-500 dark:text-slate-400" />
            <span className="min-w-0 flex-1 truncate text-[9px] text-slate-400">{t("HOME_CAPABILITIES_PREVIEW_CHAT_PLACEHOLDER")}</span>
            <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-slate-900 text-white dark:bg-slate-100 dark:text-slate-900"><ArrowUp className="h-3 w-3" /></span>
          </div>
        </div>
      </div>
    </div>
  );
}

function InterviewsPreview() {
  const { t } = useTranslation();

  return (
    <div className="h-full px-6 pt-9">
      <div className={panel}>
        <div className={panelHeader}><span className="flex items-center gap-2"><Mic className="h-3.5 w-3.5" />{t("HOME_CAPABILITIES_PREVIEW_MOCK")}</span><span className="h-1.5 w-1.5 rounded-full bg-emerald-500" /></div>
        <div className="space-y-4 p-4">
          <p className="text-[11px] leading-relaxed">{t("HOME_CAPABILITIES_PREVIEW_QUESTION")}</p>
          <div className="flex h-12 items-center justify-center gap-1">
            {[10, 18, 26, 16, 34, 24, 40, 28, 18, 30, 14, 8].map((height, index) => <span key={index} style={{ height }} className="w-1 rounded-full bg-indigo-300 dark:bg-indigo-400" />)}
          </div>
          <div className="rounded-lg bg-indigo-50 px-3 py-2.5 text-[10px] leading-relaxed text-indigo-700 dark:bg-indigo-400/10 dark:text-indigo-200">{t("HOME_CAPABILITIES_PREVIEW_TIP")}</div>
        </div>
      </div>
    </div>
  );
}

export function CapabilitiesSection() {
  const { t } = useTranslation();

  return (
    <section id="capabilities" aria-labelledby="capabilities-title" className="relative py-6 sm:py-10">
      <div className="px-1 pb-12 sm:px-4 sm:pb-16 lg:px-7">
        <HomeSection
          align="left"
          badgeText="HOME_CAPABILITIES_BADGE"
          subtitle="HOME_CAPABILITIES_SUBTITLE"
          title="HOME_CAPABILITIES_TITLE"
          titleSize="compact"
          titleId="capabilities-title"
        />
      </div>
      <div className="grid gap-x-4 gap-y-10 sm:grid-cols-2 lg:grid-cols-3 lg:gap-y-14">
        {capabilities.map(({ key, Preview, color }) => (
          <article key={key} className="min-w-0">
            <div className={`${styles.canvas} ${color} border border-white/60 dark:border-white/10`}>
              <h3 className="relative z-10 px-5 pt-5 text-xl leading-tight tracking-[-0.03em] text-slate-900 dark:text-white xl:text-2xl">{t(`HOME_CAPABILITIES_${key}_TITLE`)}</h3>
              <div aria-hidden="true" className="relative h-[280px] text-left"><Preview /></div>
            </div>
            <p className="mt-4 px-0.5 text-base leading-[1.4] tracking-[-0.015em] text-slate-600 dark:text-slate-300">{t(`HOME_CAPABILITIES_${key}_DESC`)}</p>
          </article>
        ))}
      </div>
    </section>
  );
}
